import { namespace } from "@/context";

type LargestContentfulPaintEntry = PerformanceEntry & {
  size: number;
  renderTime: number;
  loadTime: number;
  startTime: number;
};

const round = (value?: number) =>
  typeof value === "number" && !Number.isNaN(value) ? Math.round(value) : 0;

const getDuration = (
  modern?: number,
  legacyEnd?: number,
  legacyStart?: number,
) => {
  if (typeof modern === "number" && modern > 0) return modern;
  if (
    typeof legacyEnd === "number" &&
    typeof legacyStart === "number" &&
    legacyEnd >= legacyStart
  ) {
    return legacyEnd - legacyStart;
  }
  return 0;
};

export const collectPagePerformancePayload = () => {
  if (typeof window === "undefined" || typeof performance === "undefined")
    return null;
  if (!performance.getEntriesByType) return null;

  const navigationEntry = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  const timing = performance.timing;

  const pageLoadTime = getDuration(
    navigationEntry?.loadEventEnd,
    timing?.loadEventEnd,
    timing?.navigationStart,
  );
  const domReadyTime = getDuration(
    navigationEntry?.domContentLoadedEventEnd,
    timing?.domContentLoadedEventEnd,
    timing?.navigationStart,
  );
  const ttfb = getDuration(
    navigationEntry?.responseStart,
    timing?.responseStart,
    timing?.navigationStart,
  );

  const lcpEntries = performance.getEntriesByType(
    "largest-contentful-paint",
  ) as LargestContentfulPaintEntry[];
  const lcp = lcpEntries.length
    ? lcpEntries[lcpEntries.length - 1].startTime
    : 0;

  const paintEntries = performance.getEntriesByType(
    "paint",
  ) as PerformanceEntry[];
  const fcp =
    paintEntries.find((entry) => entry.name === "first-contentful-paint")
      ?.startTime ?? 0;

  return {
    page_url: window.location.href,
    page_load_time: round(pageLoadTime),
    dom_ready_time: round(domReadyTime),
    ttfb: round(ttfb),
    lcp: round(lcp),
    fcp: round(fcp),
    is_lcp_slow: lcp > 5000,
    is_page_load_slow: pageLoadTime > 5000,
  };
};

const pathToNamespaceMap: Array<{
  test: (pathname: string) => boolean;
  ns: namespace;
}> = [
  { test: (p) => p.startsWith("/pro/detail"), ns: namespace.detail },
  { test: (p) => p.startsWith("/pro/edit"), ns: namespace.edit },
  { test: (p) => p.startsWith("/pro/devices"), ns: namespace.devices },
  { test: (p) => p.startsWith("/pro/device-item"), ns: namespace.device },
  { test: (p) => p.startsWith("/pro/activity"), ns: namespace.activity },
  { test: (p) => p.startsWith("/pro/consult"), ns: namespace.consult },
  { test: (p) => p.startsWith("/pro/square"), ns: namespace.square },
  { test: (p) => p.startsWith("/pro/profile"), ns: namespace.profile },
  { test: (p) => p.startsWith("/pro/group"), ns: namespace.group },
  { test: (p) => p.startsWith("/pro/search"), ns: namespace.search },
  { test: (p) => p.startsWith("/pro/app"), ns: namespace.app },
];

export const resolvePerformanceNamespace = (pathname: string): namespace => {
  const normalized = pathname?.replace(/\/+$/, "") || "/";

  const matched = pathToNamespaceMap.find(({ test }) => test(normalized));
  if (matched) return matched.ns;

  // 覆盖首页、'/'、'/pro' 等
  if (
    normalized === "/" ||
    normalized === "/pro" ||
    normalized === "/pro/home" ||
    normalized === "/home"
  ) {
    return namespace.home;
  }

  return namespace.home;
};
